// Pure dartboard math - no DOM, no rendering. Everything here is derived
// straight from the real dartboard.svg artwork (viewBox "0 0 500 500", a
// bull centered at 250,250), so a point computed here lines up exactly with
// where components/dartboard-input.js would draw it. That file only ever
// goes number/ring -> point (for drawing a human's tap) or relies on *which
// DOM element* was clicked to know a score - it never goes point -> score.
// This module adds that reverse direction, which an AI needs: it "throws"
// at a computed (x, y), and the engine has to derive what that actually hit.
//
// Wedge layout: WEDGE_ORDER[i] sits centered at angle i * WEDGE_ANGLE_DEG
// clockwise from straight up (0deg), same convention already used by
// games/killer/controller.js and games/snooker/controller.js.
export const BOARD_CENTER = 250;
export const WEDGE_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];
export const WEDGE_ANGLE_DEG = 360 / WEDGE_ORDER.length; // 18

// Ring radii, in the SVG's own viewBox units - read directly off
// dartboard.svg's own path/circle data (bullseye & outer-bull are plain
// <circle r="...">; the wedge rings are drawn as arcs at these same radii).
export const RADII = {
    bullseye: 20,     // r=20 <circle class="bullseye">
    outerBull: 40,    // r=40 <circle class="outer-bull">
    tripleInner: 112.5,
    tripleOuter: 137.5,
    doubleInner: 187.5,
    doubleOuter: 212.5 // beyond this is a miss - off the scoring area entirely
};

function normalizeAngleDeg(deg) {
    const wrapped = deg % 360;
    return wrapped < 0 ? wrapped + 360 : wrapped;
}

// The wedge whose center angle is closest to `angleDeg` (clockwise from
// straight up) - every wedge spans WEDGE_ANGLE_DEG, so this is just the
// nearest multiple, wrapped back into WEDGE_ORDER's range.
function wedgeNumberAtAngle(angleDeg) {
    const index = Math.round(normalizeAngleDeg(angleDeg) / WEDGE_ANGLE_DEG) % WEDGE_ORDER.length;
    return WEDGE_ORDER[index];
}

// The angle (clockwise from straight up) of the given wedge number's own
// center - the inverse of wedgeNumberAtAngle, used to place an aim point.
function angleForWedgeNumber(number) {
    const index = WEDGE_ORDER.indexOf(number);
    return index * WEDGE_ANGLE_DEG;
}

// Where the center of a given scoring zone actually is, in board
// coordinates - the point a real player is "aiming at" when they call a
// target a treble 20, double 16, the bull, etc. `ring` is one of
// 'single' (the outer bed, between the triple ring and the double ring -
// the larger and far more commonly-aimed-at of the two single beds),
// 'innerSingle' (the smaller bed between the bull and the triple ring),
// 'triple', 'double', 'outerBull', or 'bullseye' (number is ignored for
// the two bull rings - the bull has no wedge).
export function targetPoint({ number, ring }) {
    if (ring === 'bullseye') return { x: BOARD_CENTER, y: BOARD_CENTER };
    if (ring === 'outerBull') {
        const r = (RADII.bullseye + RADII.outerBull) / 2;
        return { x: BOARD_CENTER, y: BOARD_CENTER - r }; // straight up - the bull has no meaningful angle
    }

    const angleDeg = angleForWedgeNumber(number);
    let radius;
    if (ring === 'triple') radius = (RADII.tripleInner + RADII.tripleOuter) / 2;
    else if (ring === 'double') radius = (RADII.doubleInner + RADII.doubleOuter) / 2;
    else if (ring === 'innerSingle') radius = (RADII.outerBull + RADII.tripleInner) / 2;
    else radius = (RADII.tripleOuter + RADII.doubleInner) / 2; // 'single' (the outer bed) - the default scoring target

    const angleRad = (angleDeg * Math.PI) / 180;
    return {
        x: BOARD_CENTER + radius * Math.sin(angleRad),
        y: BOARD_CENTER - radius * Math.cos(angleRad)
    };
}

// The real dartboard.svg coloring: single beds alternate black/white and
// double/triple rings alternate red/green, in lockstep with the same
// even/odd WEDGE_ORDER index (section-20 is black+red, section-1 is
// white+green, section-18 is black+red again, ...); the bull is its own
// pair, always the same regardless of wedge - the bullseye is red and the
// outer bull is green (dartboard.svg's own <circle> fills, #CC0000 and
// #008000) - see dartboard.svg's own per-section fill colors, which this is
// read directly off of. Used by games/halveit/ai.js's "3 Colors" round, the
// one challenge that genuinely depends on which real color a dart landed in
// rather than just which number/ring. Returns null only for a genuine miss
// (no wedge, no bull, so no color).
export function wedgeColor(number, ring) {
    if (ring === 'miss' || number == null) return null;
    if (ring === 'bullseye') return 'red';
    if (ring === 'outerBull') return 'green';
    const isBlackRedWedge = WEDGE_ORDER.indexOf(number) % 2 === 0;
    const isRinged = ring === 'double' || ring === 'triple';
    if (isBlackRedWedge) return isRinged ? 'red' : 'black';
    return isRinged ? 'green' : 'white';
}

// The reverse of targetPoint - given a point in board coordinates (a dart's
// actual landing spot), what it actually scored. `mult` is 1/2/3 (or 1/2 for
// the bull's outer/bullseye, matching mountDartboardFullBoard's own onTap
// convention: number 25, mult 1 = outer bull worth 25, mult 2 = bullseye
// worth 50). `score` is number * mult, 0 for a miss off the board entirely.
export function resolvePoint({ x, y }) {
    const dx = x - BOARD_CENTER;
    const dy = y - BOARD_CENTER;
    const radius = Math.hypot(dx, dy);

    if (radius <= RADII.bullseye) {
        return { number: 25, ring: 'bullseye', mult: 2, score: 50 };
    }
    if (radius <= RADII.outerBull) {
        return { number: 25, ring: 'outerBull', mult: 1, score: 25 };
    }
    if (radius > RADII.doubleOuter) {
        return { number: null, ring: 'miss', mult: 0, score: 0 };
    }

    // atan2(dx, -dy): straight up (dy negative, dx 0) is angle 0, increasing
    // clockwise - matches angleForWedgeNumber's own convention.
    const angleDeg = (Math.atan2(dx, -dy) * 180) / Math.PI;
    const number = wedgeNumberAtAngle(angleDeg);

    let ring, mult;
    if (radius <= RADII.tripleInner) { ring = 'innerSingle'; mult = 1; }
    else if (radius <= RADII.tripleOuter) { ring = 'triple'; mult = 3; }
    else if (radius <= RADII.doubleInner) { ring = 'single'; mult = 1; }
    else { ring = 'double'; mult = 2; }

    return { number, ring, mult, score: number * mult };
}
